import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';

const DriverStoriesSection = () => {
  const driverStories = [
    {
      name: 'Ramesh K',
      role: 'Full-time Driver',
      location: 'Chennai',
      review: "Nura has given me stable earnings and peace of mind. Weekly payouts are always on time, and I don't have to worry about vehicle ownership or maintenance.",
    },
    {
      name: 'Lakshmi S',
      role: 'Full-time Driver',
      location: 'Chennai',
      review: "I drive with Nura every day and the work is consistent. The electric auto is comfortable, and support from the team is quick whenever I need help.",
    },
    {
      name: 'Arjun M',
      role: 'Part-time Driver | College Student',
      location: 'Chennai',
      review: "I drive with Nura after college hours. It's flexible, easy to manage, and helps me earn extra money without affecting my studies.",
    },
    {
      name: 'Sneha R',
      role: 'Part-time Driver | College Student',
      location: 'Chennai',
      review: "Nura lets me choose when I want to work. I mostly drive on weekends, and the earnings help cover my expenses while studying.",
    },
  ];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [slidesPerView, setSlidesPerView] = useState(1);

  // Determine slides per view based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSlidesPerView(2);
      } else {
        setSlidesPerView(1);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const totalSlides = Math.ceil(driverStories.length / slidesPerView);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  // Auto-advance every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [currentSlide, totalSlides]);

  // Get visible stories for current slide
  const getVisibleStories = () => {
    const startIndex = currentSlide * slidesPerView;
    return driverStories.slice(startIndex, startIndex + slidesPerView);
  };

  return (
    <section className="py-16 bg-black">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Driver Stories from the <span className="text-[#bceb39]">Nura Community</span>
          </h2>
          <p className="text-lg text-gray-400">
            Real experiences from people driving with Nura every day.
          </p>
        </div>

        {/* Carousel */}
        <div className="relative">
          <div className="flex gap-6">
            {getVisibleStories().map((story, index) => (
              <div
                key={`${currentSlide}-${index}`}
                className="flex-1 min-w-0 bg-gray-900 rounded-2xl p-6 border border-gray-800 transition-all duration-500"
              >
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={18} className="text-[#bceb39] fill-[#bceb39]" />
                  ))}
                </div>

                {/* Review Text */}
                <p className="text-gray-300 text-base leading-relaxed mb-6 min-h-[100px]">
                  "{story.review}"
                </p>

                {/* Driver Info */}
                <div className="border-t border-gray-800 pt-4">
                  <p className="text-white font-semibold text-lg">{story.name}</p>
                  <p className="text-[#bceb39] text-sm">{story.role}</p>
                  <p className="text-gray-500 text-sm">{story.location}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute -left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#bceb39] text-black rounded-full flex items-center justify-center hover:bg-[#a8d632] transition-all duration-300 shadow-lg z-10"
            aria-label="Previous slide"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={nextSlide}
            className="absolute -right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#bceb39] text-black rounded-full flex items-center justify-center hover:bg-[#a8d632] transition-all duration-300 shadow-lg z-10"
            aria-label="Next slide"
          >
            <ChevronRight size={20} />
          </button>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-8">
            {[...Array(totalSlides)].map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-2 rounded-full transition-all duration-300 ${index === currentSlide ? 'bg-[#bceb39] w-8' : 'bg-gray-600 w-2'
                  }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DriverStoriesSection;
